import { create } from 'zustand';
import type { ChatMessage, DrinkKind, Look, RoomKind, SelfProfile } from '@kopi-kaki/protocol';
import type { CloseReason, SocketStatus } from '../game/socket.js';
import { storage } from '../lib/storage.js';

/**
 * Built-in windows, plus any id a feature module chooses for its own windows
 * (prefix them with the feature name, e.g. 'games:table').
 */
export type WindowId =
  | 'navigator'
  | 'shop'
  | 'inventory'
  | 'wardrobe'
  | 'leaderboard'
  | 'help'
  | 'account'
  | 'drinks'
  | 'chatlog'
  | 'report'
  | (string & {});

export interface ChatLine {
  id: string;
  kind: 'chat' | 'system';
  name: string;
  body: string;
  at: number;
  avatarId?: string;
}

export interface Toast {
  id: number;
  tone: 'info' | 'success' | 'warning';
  message: string;
}

export type Selection = { kind: 'avatar'; id: string } | { kind: 'furni'; id: string } | null;

export interface OccupantInfo {
  id: string;
  name: string;
  look: Look;
  /** A scripted guide (Bot Kaki), not a player. */
  npc?: boolean;
}

export interface CurrentRoom {
  id: string;
  name: string;
  ownerId: string | null;
  ownerName: string | null;
  kind: RoomKind;
  /** Seed key of the room's guide script, if it has a Bot Kaki. */
  guideKey?: string | null;
  /** Stable seed key of a public room (e.g. 'dog_run'), for room-specific features. */
  seedKey?: string | null;
  description: string;
  wallStyle: string;
  floorStyle: string;
  /** Ids of players the owner trusts to move furniture and operate doors. */
  rights: string[];
}

export interface Placement {
  /** Set when repositioning a placed item rather than placing one from the inventory. */
  moveId?: string;
  inventoryId: string;
  /** Further inventory ids of the same kind, so placing keeps going until they run out. */
  queue?: string[];
  kind: string;
  rotation: number;
}

interface GameStore {
  session: 'loading' | 'anonymous' | 'authenticated';
  self: SelfProfile | null;
  connection: SocketStatus;
  closeReason: CloseReason;
  /** Kaki Dollars and respects left to give today. */
  wallet: { money: number; respectsLeft: number; gold: number; diamond: number };
  /** Players whose chat is hidden locally, by avatar id. */
  ignored: Record<string, true>;
  /** What the report dialog is about, when open. */
  reporting: ReportTarget | null;
  room: CurrentRoom | null;
  occupants: Record<string, OccupantInfo>;
  chat: ChatLine[];
  selection: Selection;
  windows: Partial<Record<WindowId, boolean>>;
  placing: Placement | null;
  toasts: Toast[];
  inventoryVersion: number;
  roomsVersion: number;
  /** Bumped when furniture in the current room changes. */
  furniVersion: number;
  muted: boolean;
  /** The player's own avatar state, for toggle buttons. */
  me: { dancing: boolean; afk: boolean; carrying: DrinkKind | null };
}

const MAX_CHAT_LINES = 120;
const IGNORED_KEY = 'kk.ignored';

export type ReportReason = 'spam' | 'harassment' | 'inappropriate' | 'cheating' | 'other';

export interface ReportTarget {
  targetType: 'user' | 'room' | 'chat';
  targetId: string;
  /** Shown in the dialog, e.g. the player's name or the chat line. */
  label: string;
}

function loadIgnored(): Record<string, true> {
  try {
    const raw = storage.get(IGNORED_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(ids)) return {};
    return Object.fromEntries(ids.filter((id): id is string => typeof id === 'string').map((id) => [id, true]));
  } catch {
    return {};
  }
}

let toastSeq = 0;

export const useGame = create<GameStore>(() => ({
  session: 'loading',
  self: null,
  connection: 'idle',
  closeReason: null,
  wallet: { money: 0, respectsLeft: 0, gold: 0, diamond: 0 },
  ignored: loadIgnored(),
  reporting: null,
  room: null,
  occupants: {},
  chat: [],
  selection: null,
  windows: {},
  placing: null,
  toasts: [],
  inventoryVersion: 0,
  roomsVersion: 0,
  furniVersion: 0,
  muted: storage.get('kk.muted') === '1',
  me: { dancing: false, afk: false, carrying: null },
}));

/* -------------------------------------------------------------------------- */
/* Actions -- plain functions, usable from React and from the game engine.     */
/* -------------------------------------------------------------------------- */

export function setSelf(self: SelfProfile | null): void {
  useGame.setState({
    self,
    session: self ? 'authenticated' : 'anonymous',
    ...(self ? { wallet: { money: self.money, respectsLeft: self.respectsLeft, gold: self.gold, diamond: self.diamond } } : {}),
  });
}

export function openWindow(id: WindowId): void {
  useGame.setState((s) => ({ windows: { ...s.windows, [id]: true } }));
}

export function closeWindow(id: WindowId): void {
  useGame.setState((s) => ({ windows: { ...s.windows, [id]: false } }));
}

export function toggleWindow(id: WindowId): void {
  useGame.setState((s) => ({ windows: { ...s.windows, [id]: !s.windows[id] } }));
}

export function pushToast(message: string, tone: Toast['tone'] = 'info'): void {
  const id = ++toastSeq;
  useGame.setState((s) => ({ toasts: [...s.toasts.slice(-3), { id, tone, message }] }));
  // Long messages (a DM preview, a server notice) need time to be read in full.
  window.setTimeout(() => dismissToast(id), Math.min(10000, (tone === 'warning' ? 4500 : 3500) + message.length * 40));
}

export function dismissToast(id: number): void {
  useGame.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}

export function pushChat(line: ChatLine): void {
  useGame.setState((s) => {
    if (s.chat.some((existing) => existing.id === line.id)) return s;
    return { chat: [...s.chat.slice(-(MAX_CHAT_LINES - 1)), line] };
  });
}

export function pushSystemChat(body: string): void {
  pushChat({ id: `sys-${Date.now()}-${Math.random()}`, kind: 'system', name: '', body, at: Date.now() });
}

export function chatLineFrom(message: ChatMessage): ChatLine {
  return {
    id: message.id,
    kind: 'chat',
    name: message.name,
    body: message.body,
    at: message.at,
    avatarId: message.avatarId,
  };
}

export function select(selection: Selection): void {
  useGame.setState({ selection });
}

export function setMuted(muted: boolean): void {
  storage.set('kk.muted', muted ? '1' : '0');
  useGame.setState({ muted });
}

export function bumpInventory(): void {
  useGame.setState((s) => ({ inventoryVersion: s.inventoryVersion + 1 }));
}

export function bumpRooms(): void {
  useGame.setState((s) => ({ roomsVersion: s.roomsVersion + 1 }));
}

export function isRoomOwner(state: Pick<GameStore, 'room' | 'self'>): boolean {
  return Boolean(state.room?.ownerId && state.self && state.room.ownerId === state.self.id);
}

/** Owner, or a player the owner gave rights to move furniture and operate doors. */
export function canMoveFurni(state: Pick<GameStore, 'room' | 'self'>): boolean {
  return isRoomOwner(state) || Boolean(state.self && state.room?.rights.includes(state.self.id));
}

/** Hide (or unhide) a player's chat on this device. */
export function setIgnored(avatarId: string, ignored: boolean): void {
  useGame.setState((s) => {
    const next = { ...s.ignored };
    if (ignored) next[avatarId] = true;
    else delete next[avatarId];
    storage.set(IGNORED_KEY, JSON.stringify(Object.keys(next)));
    return {
      ignored: next,
      // Drop what they already said from the visible log too.
      chat: ignored ? s.chat.filter((line) => line.avatarId !== avatarId) : s.chat,
    };
  });
}

export function openReport(target: ReportTarget): void {
  useGame.setState((s) => ({ reporting: target, windows: { ...s.windows, report: true } }));
}
